/**
 * SetupGuide — backend-driven setup checklist (ADR-019 §D8).
 *
 * Replaces the 4 hardcoded ``setupSteps: [...]`` arrays on the
 * Overview pages. Renders a horizontal stepper with each step
 * surfaced as a small card. State comes from
 * ``GET /v1/onboarding/steps?section=...``; the user lands in
 * each step's setup page by clicking the card.
 *
 * UX principles (P1.1 redesign):
 *
 *   * Step titles are NEVER truncated. The setup catalog is curated
 *     copy; cutting it off with ``...`` defeats the entire point of
 *     the widget. Titles wrap to 2 lines max, then ellipsis as a
 *     last resort. Each step card has enough width that the common
 *     case (~30 chars) always renders inline.
 *
 *   * No "Dismiss this step" affordance. The setup flow is
 *     intentionally environed — skipping a step means the next
 *     step's prerequisites aren't met. Surfacing a dismiss button
 *     teaches the user that skipping is a valid mode, which is
 *     wrong. Dismissed state still exists on the API for the
 *     operator console and is filtered out here.
 *
 *   * No three-dot menu. The only manual override that matters
 *     (``Mark complete``) is exposed as a hover-revealed checkmark
 *     IconButton anchored to the step card's top-right corner.
 *     Same pattern Dashboard widgets use for their per-widget
 *     actions.
 *
 *   * Click anywhere on any card → ``navigate(step.path)``. Completed
 *     steps remain useful entry points for reviewing or changing configuration;
 *     completion is a status, not a navigation lock.
 *
 *   * Done steps render with a soft success tint and an inline
 *     check icon while keeping the same navigable cursor and hover affordance.
 *
 * Visibility: a step's ``done`` is computed server-side per the
 * precedence ladder (manual → dismiss → auto → pending). Steps
 * with badge='dismissed' are hidden from the inline tile row but
 * counted in the ``dismissedCount`` so admins can see "3 dismissed"
 * and one-click resurface them via the TopNav reset action.
 *
 * Usage::
 *
 *   <SetupGuide section="attendance" />
 *
 * The ``onSnackbar`` prop is optional; pages that already have a
 * Snackbar can wire it. Otherwise the component shows nothing on
 * success/failure other than re-fetching the list.
 */
import React, { useCallback, useEffect, useState } from 'react';
import GetAppDialog from '../face/GetAppDialog';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CheckOutlined,
  CheckCircleOutlined,
  CloseOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ApiError, onboardingApi, type OnboardingSection, type OnboardingStep } from '../../api';
import { translateApiError } from '../../i18n/translateApiError';


export interface SetupGuideProps {
  section: OnboardingSection;
  /** Optional: page already has a Snackbar; surface success / errors via this. */
  onSnackbar?: (message: string) => void;
  /** Optional: hide the whole card when the section has 0 applicable
   *  steps. Defaults to true so the page doesn't ship an empty
   *  card on tenants whose plan filters out everything. */
  hideWhenEmpty?: boolean;
}

/** Catalog code of the step that opens the app-download dialog (svdy-platform seed_onboarding_catalog.py). */
export const GET_APP_STEP = 'get_app';

const SetupGuide: React.FC<SetupGuideProps> = ({
  section,
  onSnackbar,
  hideWhenEmpty = true,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [steps, setSteps] = useState<OnboardingStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [actingStepId, setActingStepId] = useState<string | null>(null);

  // Whole-card dismiss is per-section per-device localStorage so
  // admins who have configured one section don't see its guide on
  // every page reload. The TopNav reset action separately wipes
  // these keys + calls /steps/reset to bring back per-step
  // dismissals from the backend.
  const localKey = `setup-guide-card-dismissed:${section}`;
  const [cardVisible, setCardVisible] = useState(
    () => localStorage.getItem(localKey) !== 'true',
  );

  const fetchSteps = useCallback(async () => {
    setLoading(true);
    try {
      // `lang` MUST be sent. Without it the endpoint defaults to `en`, which is very likely why
      // a duplicate copy of every title grew in `common.json` in the first place: the API looked
      // English-only, so someone localized it on this side, and the two then drifted.
      const data = await onboardingApi.list({
        section,
        lang: i18n.resolvedLanguage ?? i18n.language,
      });
      setSteps(data);
    } catch (e) {
      onSnackbar?.(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.loadFailed'));
    } finally {
      setLoading(false);
    }
    // `resolvedLanguage` in the deps, not the `i18n` object: the instance identity is stable
    // while the language is what the request varies on, so a locale switch refetches and a
    // re-render does not (session-discipline §11).
  }, [section, onSnackbar, t, i18n.resolvedLanguage, i18n.language]);

  useEffect(() => {
    fetchSteps();
  }, [fetchSteps]);

  // Listen for the TopNav reset event so the card reappears across
  // tabs without a page refresh.
  useEffect(() => {
    const handler = () => {
      localStorage.removeItem(localKey);
      setCardVisible(true);
      fetchSteps();
    };
    window.addEventListener('show-setup-guide', handler);
    return () => window.removeEventListener('show-setup-guide', handler);
  }, [fetchSteps, localKey]);

  const visibleSteps = steps.filter((s) => s.badge !== 'dismissed');
  const doneCount = visibleSteps.filter((s) => s.done).length;
  const total = visibleSteps.length;
  const dismissedCount = steps.length - visibleSteps.length;

  // The guide's last step is not a page to configure: it is getting the team onto the phone,
  // where attendance and requests actually happen. It opens the download dialog instead of
  // navigating; its catalog `path` (the workers list) remains the fallback for any client
  // that does not know the code.
  const [getAppOpen, setGetAppOpen] = useState(false);
  const handleStepClick = (step: OnboardingStep) => {
    if (step.code === GET_APP_STEP) {
      setGetAppOpen(true);
      return;
    }
    navigate(step.path);
  };

  const handleDismissCard = () => {
    localStorage.setItem(localKey, 'true');
    setCardVisible(false);
  };

  const handleMarkComplete = async (stepId: string, e: React.MouseEvent) => {
    // Stop the click from also firing handleStepClick on the parent
    // card — the user is explicitly marking complete, not navigating
    // to the step's setup page.
    e.stopPropagation();
    setActingStepId(stepId);
    try {
      await onboardingApi.complete(stepId);
      onSnackbar?.(t('common:setupGuide.stepMarkedComplete'));
      await fetchSteps();
    } catch (err) {
      onSnackbar?.(err instanceof ApiError ? translateApiError(err, t) : t('common:feedback.actionFailed'));
    } finally {
      setActingStepId(null);
    }
  };

  // Hide entirely when the user dismissed the card AND nothing
  // dismissed at the server level — there's nothing to surface.
  if (!cardVisible) return null;
  if (loading) {
    return (
      <Paper elevation={0} sx={{ width: '100%', mb: 2, borderRadius: 2, border: '1px solid', borderColor: (theme) => alpha(theme.palette.primary.main, 0.24), p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">{t('common:setupGuide.title')}…</Typography>
      </Paper>
    );
  }
  if (hideWhenEmpty && total === 0 && dismissedCount === 0) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%', mb: 2, borderRadius: 2,
        border: '1px solid', borderColor: (theme) => alpha(theme.palette.primary.main, 0.24),
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box sx={{
        px: 2.5, py: 1.25, display: 'flex',
        justifyContent: 'space-between', alignItems: 'center',
        borderBottom: '1px solid', borderColor: 'divider',
        gap: 1.5,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
            {t('common:setupGuide.title')}
          </Typography>
          {total > 0 && doneCount < total && (
            <Chip
              label={t('common:setupGuide.stepsRemaining', { count: total - doneCount })}
              size="small" color="warning" variant="outlined"
              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
            />
          )}
          {total > 0 && doneCount === total && (
            <Chip
              label={t('common:setupGuide.allDone')}
              size="small" color="success"
              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
            />
          )}
          {total > 0 && (
            <LinearProgress
              variant="determinate"
              value={(doneCount / total) * 100}
              sx={{ width: 80, height: 4, borderRadius: 8, bgcolor: 'rgba(0,0,0,0.08)' }}
            />
          )}
          {dismissedCount > 0 && (
            <Tooltip title={t('common:setupGuide.reEnableTooltip')}>
              <Chip
                label={t('common:setupGuide.hiddenChip', { count: dismissedCount })}
                size="small" variant="outlined"
                sx={{ height: 20, fontSize: '0.65rem' }}
              />
            </Tooltip>
          )}
        </Box>
        <Tooltip title={t('common:setupGuide.hideTooltip')}>
          <IconButton aria-label={t('common:actions.close')} size="small" onClick={handleDismissCard} sx={{ p: 0.25 }}>
            <CloseOutlined sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Body */}
      {total === 0 ? (
        <Box sx={{ px: 2.5, py: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {t('common:setupGuide.emptyDoneState')}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            px: 2.5, py: 2,
            display: 'flex', alignItems: 'stretch',
            gap: 0,
            // Wrap to a second row when there are too many steps
            // to fit comfortably side-by-side (e.g. attendance has 6).
            // The row gap keeps wrapped rows from touching.
            flexWrap: 'wrap',
            rowGap: 2,
          }}
        >
          {visibleSteps.map((step, i) => {
            const isActing = actingStepId === step.id;
            const isLast = i === visibleSteps.length - 1;
            const nextDone = !isLast && visibleSteps[i + 1]?.done;
            return (
              <React.Fragment key={step.id}>
                <StepCard
                  step={step}
                  index={i}
                  isActing={isActing}
                  onClick={() => handleStepClick(step)}
                  onMarkComplete={(e) => handleMarkComplete(step.id, e)}
                />
                {!isLast && (
                  <Box
                    sx={{
                      flex: '0 0 16px',
                      alignSelf: 'center',
                      height: 2,
                      mx: 0.5,
                      // Connector turns success-green when EITHER end
                      // is done — visually communicates flow progress.
                      bgcolor: step.done || nextDone ? 'success.main' : 'rgba(0,0,0,0.12)',
                      borderRadius: 1,
                      transition: 'background-color 0.3s',
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Box>
      )}
      <GetAppDialog open={getAppOpen} onClose={() => setGetAppOpen(false)} />
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// StepCard — one tile per setup step. Hover reveals the manual
// "Mark complete" affordance; click anywhere navigates to the
// step's setup page (handled by parent). Done steps tint green
// but remain navigable for review and later edits.
// ---------------------------------------------------------------------------

interface StepCardProps {
  step: OnboardingStep;
  index: number;
  isActing: boolean;
  onClick: () => void;
  onMarkComplete: (e: React.MouseEvent) => void;
}

const StepCard: React.FC<StepCardProps> = ({
  step,
  index,
  isActing,
  onClick,
  onMarkComplete,
}) => {
  const { t } = useTranslation();
  const done = step.done;

  return (
    <Box
      onClick={onClick}
      sx={{
        // flex: 1 lets cards share the row equally; min/max keep
        // each tile within a sensible width so titles render on
        // 1-2 lines instead of being squeezed to one cramped column.
        flex: '1 1 140px',
        minWidth: 140,
        maxWidth: 200,
        p: 1.25,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: done ? 'rgba(76,175,80,0.4)' : 'divider',
        bgcolor: done ? 'rgba(76,175,80,0.06)' : 'background.paper',
        cursor: 'pointer',
        position: 'relative',
        transition: 'border-color 0.15s, background-color 0.15s, transform 0.15s',
        // Every card remains a route back to its setup page. Pending cards also
        // reveal the manual completion action; completed cards only change edge/tint.
        '&:hover': done
          ? {
              borderColor: 'success.main',
              bgcolor: 'rgba(76,175,80,0.1)',
            }
          : {
              borderColor: 'primary.main',
              bgcolor: 'rgba(0,113,227,0.04)',
              '& .step-mark-complete': { opacity: 1 },
            },
      }}
    >
      {/* The DESCRIPTION, via a tooltip, because the card holds two lines of title and no more.
          It was returned by `/v1/onboarding/steps` and rendered nowhere — so the sentence that
          tells an admin WHY a step matters (for `processes_roles`, that a workflow cannot go live
          while a role it routes to is empty) was unreadable, while `seed_onboarding_catalog.py`
          raised SystemExit if any of its nine locales was missing. A guard on copy with no
          surface. `describeChild` so the tooltip does not replace the card's accessible name. */}
      <Tooltip title={step.description || ''} placement="top" describeChild>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        {/* Numbered circle / done check / in-flight spinner */}
        <Box
          sx={{
            width: 26, height: 26, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            bgcolor: done ? 'success.main' : 'background.paper',
            border: '2px solid',
            borderColor: done ? 'success.main' : 'primary.main',
          }}
        >
          {isActing ? (
            <CircularProgress size={11} sx={{ color: 'primary.main' }} />
          ) : done ? (
            <CheckCircleOutlined sx={{ fontSize: '0.85rem', color: 'white' }} />
          ) : (
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.7rem' }}
            >
              {index + 1}
            </Typography>
          )}
        </Box>

        {/* Title — full text, wraps up to 2 lines, then ellipsis. */}
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            fontSize: '0.75rem',
            color: done ? 'text.disabled' : 'text.primary',
            lineHeight: 1.35,
            // Show the full title on 1-2 lines; only fall back to
            // ellipsis if a single word is genuinely too long for
            // the cell. Setup catalog titles are short by policy
            // (≤40 chars) so this almost never trips.
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
            flex: 1,
            minWidth: 0,
            // Ensure the row matches the circle's vertical centre
            // when the title is a single line.
            pt: 0.25,
          }}
        >
          {step.title}
        </Typography>
      </Box>
      </Tooltip>

      {/* Hover-revealed manual override: mark complete. NOT a menu —
          a direct icon button, mirroring Dashboard's per-widget
          action style. Hidden on done steps (nothing to do) and on
          touch devices the parent click still works to navigate. */}
      {!done && !isActing && (
        <Tooltip title={t('common:setupGuide.markCompleteTooltip')}>
          <IconButton
            className="step-mark-complete"
            size="small"
            onClick={onMarkComplete}
            aria-label={t('common:setupGuide.markStepCompleteAria', {
              title: step.title,
            })}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              p: 0.25,
              opacity: 0,
              transition: 'opacity 0.15s',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'success.main',
              color: 'success.main',
              '&:hover': {
                bgcolor: 'success.main',
                color: 'white',
              },
            }}
          >
            <CheckOutlined sx={{ fontSize: '0.8rem' }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default SetupGuide;
